import { QRCodeSVG } from 'qrcode.react';
import {
    DEFAULT_PASS_BACKGROUND_COLOR,
    DEFAULT_PASS_TEXT_COLOR,
} from '../../../shared/constants/loyalPassBrand';

function absoluteUrl(raw) {
    const t = (raw || '').trim();
    if (!t) {
        return '';
    }
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

/**
 * Approximate Apple Wallet `storeCard` face layout (logo + logoText, strip, primary, secondary, QR).
 * Apple may still change fonts, weights, and spacing on device.
 * No thumbnail slot — PassKit art is strip/background + fields only.
 */
const DEFAULT_PREVIEW_FOOTER =
    'Approximate Apple Wallet layout. Your server fetches image URLs when building the pass; fonts and spacing on device may differ slightly.';

export default function AppleWalletPassPreview({
    merchantName,
    programName,
    logoUrl,
    stripImageUrl,
    backgroundImageUrl,
    passBackgroundColor = DEFAULT_PASS_BACKGROUND_COLOR,
    passTextColor = DEFAULT_PASS_TEXT_COLOR,
    points = 0,
    visits = 0,
    barcodeSampleMessage = '00000000-0000-0000-0000-000000000000',
    barcodeAltText = 'customer@example.com',
}) {
    const logoSrc = absoluteUrl(logoUrl);
    const stripSrc = absoluteUrl(stripImageUrl);
    const bgSrc = absoluteUrl(backgroundImageUrl);

    return (
        <div className="w-full max-w-[320px]">
            <div
                className="relative overflow-hidden rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/10"
                style={{ backgroundColor: passBackgroundColor }}
            >
                {bgSrc ? (
                    <div
                        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40"
                        style={{ backgroundImage: `url(${bgSrc})` }}
                        aria-hidden
                    />
                ) : null}
                <div className="relative z-[1] px-3 pb-3 pt-2.5 text-left">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
                            {logoSrc ? (
                                <img src={logoSrc} alt="" className="max-h-full max-w-full object-contain" />
                            ) : (
                                <span className="text-[10px] font-medium text-gray-500">Logo</span>
                            )}
                        </div>
                        <span
                            className="min-w-0 truncate text-[15px] font-semibold leading-tight tracking-tight"
                            style={{ color: passTextColor }}
                        >
                            {merchantName || 'Store name'}
                        </span>
                    </div>

                    {stripSrc ? (
                        <div className="-mx-1 mt-2 overflow-hidden rounded-md">
                            <img src={stripSrc} alt="" className="h-16 w-full object-cover" />
                        </div>
                    ) : (
                        <div className="mt-2 h-1.5 rounded-full bg-white/15" aria-hidden />
                    )}

                    <h3
                        className="mt-2 min-w-0 text-[22px] font-bold leading-snug tracking-tight"
                        style={{ color: passTextColor }}
                    >
                        {programName || 'Program'}
                    </h3>

                    <div className="mt-2 flex justify-between gap-4">
                        <div>
                            <p
                                className="text-[10px] font-semibold uppercase tracking-wide opacity-90"
                                style={{ color: passTextColor }}
                            >
                                Points
                            </p>
                            <p className="text-lg font-semibold tabular-nums" style={{ color: passTextColor }}>
                                {points}
                            </p>
                        </div>
                        <div className="text-right">
                            <p
                                className="text-[10px] font-semibold uppercase tracking-wide opacity-90"
                                style={{ color: passTextColor }}
                            >
                                Visits
                            </p>
                            <p className="text-lg font-semibold tabular-nums" style={{ color: passTextColor }}>
                                {visits}
                            </p>
                        </div>
                    </div>

                    <div className="mt-3 flex flex-col items-center gap-1.5">
                        <div className="rounded-lg bg-white p-2 shadow-inner">
                            <QRCodeSVG value={barcodeSampleMessage} size={112} level="M" />
                        </div>
                        <p
                            className="max-w-[148px] truncate text-center text-[11px] font-medium opacity-90"
                            style={{ color: passTextColor }}
                        >
                            {barcodeAltText}
                        </p>
                    </div>

                    <p
                        className="mt-2.5 text-center text-[10px] font-medium tracking-wide opacity-70"
                        style={{ color: passTextColor }}
                    >
                        Powered by LoyalPass
                    </p>
                </div>
            </div>
            <p className="mt-2 text-center text-[11px] leading-snug text-gray-500">{DEFAULT_PREVIEW_FOOTER}</p>
        </div>
    );
}
